import React, { useEffect, useState } from 'react';
import { Typography, CircularProgress, Paper, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageShell } from '../components/layout';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import api from '../services/api'; // Use centralized API service
import { useNotification } from '../contexts/NotificationContext';

const VerifyEmail = () => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState('');
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setError('Invalid or missing verification token.');
      setLoading(false);
      return;
    }

    const verify = async () => {
      try {
        const response = await api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`);
        const data = response.data || response;
        
        setVerified(true);
        setTimeout(() => navigate('/dashboard'), 5000);
      } catch (error) {
        // Handle API errors - use the message from ApiError or fallback
        const errorMessage = error.message || 'Verification failed. Please try again later.';
        setError(errorMessage);
        showNotification(errorMessage, 'error');
      } finally {
        setLoading(false);
      }
    };

    verify();
  }, [searchParams, navigate, showNotification]);

  return (
    <PageShell hideChat kind="form" dots containerSx={{ py: { xs: 2, sm: 8 }, justifyContent: 'center' }}>
      <Paper
        variant="modern"
        elevation={3}
        sx={{
          p: { xs: 2, sm: 5 },
          maxWidth: '100%',
          overflow: 'hidden',
          textAlign: 'center',
        }}
      >
        <Typography 
          variant="h4" 
          sx={{ 
            ...theme.modern.typography.modernHeading,
            fontWeight: 800, 
            mb: 2, 
            letterSpacing: 1 
          }}
        >
          Email Verification
        </Typography>
        {loading ? (
          <CircularProgress color="primary" size={48} sx={{ my: 4 }} />
        ) : verified ? (
          <>
            <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 64, mb: 2 }} />
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700, mb: 1 }}>
              Your email has been verified!
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
              Redirecting to login page ...
            </Typography>
            <Button
              variant="gradient"
              onClick={() => navigate('/login')}
              sx={{
                px: 4,
                py: 1.5,
                fontWeight: 600,
                fontSize: '1rem'
              }}
            >
              Go to Login
            </Button>
          </>
        ) : (
          <>
            <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 56, mb: 2 }} />
            <Typography variant="h6" color="error.main" sx={{ fontWeight: 700, mb: 1 }}>
              Verification Failed
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {error}
            </Typography>
          </>
        )}
      </Paper>
    </PageShell>
  );
};

export default VerifyEmail;